export type Theme = 'light' | 'dark';
export type Language = 'en' | 'ar';

export const LANGUAGES: readonly Language[] = ['en', 'ar'];

/**
 * Language and theme resolution (FEAT-016, REQ-089).
 *
 *   language = saved → browser → English
 *   theme    = saved → dark
 *
 * "Saved" means the value persisted server-side against the user's account and
 * returned at login, so preferences follow them across devices. The prototype
 * kept these in localStorage with a window.name fallback; that is deliberately
 * not carried over — it makes a preference device-local, which is the opposite
 * of what REQ-088 asks for. Until a user is authenticated there is no saved
 * value, so the fallbacks below are the whole answer.
 *
 * On theme, the FRS states the order as "saved → OS → dark", but the prototype
 * consults no OS setting at all: `initTheme()` returns `'dark'` whenever nothing
 * is saved. We match the prototype, for two reasons.
 *
 * First, the mandate is to preserve its behaviour. Second, the OS step cannot be
 * implemented faithfully anyway: `prefers-color-scheme` no longer has a
 * `no-preference` value, so `(prefers-color-scheme: light)` matches for every
 * user who has not explicitly chosen dark. Honouring it would put the majority
 * of users on the light theme, directly contradicting §8.4's "dark theme as the
 * unset default". The stated order and the stated default cannot both hold; the
 * default is the one the prototype and ASM-08 agree on.
 */

export function resolveLanguage(saved: Language | null | undefined): Language {
  if (saved && LANGUAGES.includes(saved)) return saved;

  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.split('-')[0]?.toLowerCase();
    if (base === 'ar') return 'ar';
    if (base === 'en') return 'en';
  }

  return 'en';
}

export function resolveTheme(saved: Theme | null | undefined): Theme {
  if (saved === 'light' || saved === 'dark') return saved;
  return 'dark';
}

/**
 * Writes the preferences onto <html>.
 *
 * `dir` drives the entire right-to-left layout. The prototype has no `[dir=rtl]`
 * selectors at all — it uses CSS logical properties (`inset-inline`,
 * `margin-inline`, `text-align: start`), so flipping this one attribute mirrors
 * the whole interface correctly with no separate stylesheet.
 */
export function applyPreferences(preferences: { language: Language; theme: Theme }): void {
  const root = document.documentElement;
  root.lang = preferences.language;
  root.dir = preferences.language === 'ar' ? 'rtl' : 'ltr';
  root.dataset.theme = preferences.theme;
}
